<template>
  <header-menu
    :class="{
      'search': true,
      'search--active': isSearchActive,
      'search--loading': loading
    }"
    :id="$options.id">
    <template v-slot:link>
      <div class="search__header">
        <button
          class="search__trigger"
          @click="onTriggerClick">
          <sprite id="app/search" />
        </button>
        <div
          v-if="isSearchActive"
          class="search__field"
          @click.stop>
          <input
            class="search__input"
            type="text"
            ref="input"
            :placeholder="$t('placeholder')"
            @input="onInput"
            @keyup.down="onInputKeyDown"
            @keyup.up="onInputKeyUp"
            @keyup.enter="onInputKeyEnter">
          <button
            class="search__close"
            @click="onCloseClick">
            <sprite
              class="search__close-sprite"
              id="app/close" />
          </button>
        </div>
      </div>
    </template>
    <template>
      <div
        class="search__menu"
        @touchstart="onTouchStart">
        <div
          v-if="results || error"
          class="search__menu-inner">
          <div
            class="search__error"
            v-if="error">
            {{ $t("error") }}
          </div>
          <ul
            v-else-if="results.length > 0"
            class="search__results">
            <li
              v-for="(item, index) in results"
              :key="item.externalId"
              :class="{
                'search__result': true,
                'search__result--active': activeResult === index
              }"
              @mouseover="activeResult = index"
              @mouseout="activeResult = null">
              <a
                :href="item.url"
                class="search__link"
                @click.stop>
                <div class="search__link-inner">
                  <h3
                    class="search__title"
                    v-html="item.title" />
                  <span
                    class="search__author"
                    v-html="`${$t('by')} ${item.author}`" />
                </div>
              </a>
            </li>
          </ul>
          <div
            class="search__no-results"
            v-else>
            {{ $t("noResults") }}
          </div>
        </div>
      </div>
    </template>
  </header-menu>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import HeaderMenu from "./header-menu";
import Sprite from "@/shared/sprite";
import "/wds/sprites/app/search";
import "/wds/sprites/app/close";
import * as location from "@/utils/location";
import debounce from "@/utils/debounce";

export default {
  name: "Search",
  id: "search",
  components: {
    HeaderMenu,
    Sprite
  },
  data() {
    return {
      activeResult: null,
      query: null
    };
  },
  computed: {
    ...mapGetters("header", ["isSearchActive"]),
    ...mapState("search", ["results", "error", "loading"])
  },
  watch: {
    isSearchActive(value) {
      if (value) {
        this.$nextTick(() => {
          this.$refs.input.focus();
        });
      } else {
        this.$store.dispatch("search/reset");
      }
    }
  },
  methods: {
    onTriggerClick() {
      this.$store.commit("header/openMenu", this.$options.id);
    },
    onCloseClick() {
      this.$store.commit("header/closeMenu");
    },
    onInput: debounce(function (e) {
      this.$store.dispatch("search/query", e.target.value);
    }),
    onInputKeyDown() {
      if (!this.results) return;

      if (this.activeResult === null) {
        this.activeResult = 0;
      } else if (this.activeResult < this.results.length - 1) {
        this.activeResult ++;
      }
    },
    onInputKeyUp() {
      if (!this.results) return;

      if (this.activeResult > 0) {
        this.activeResult --;
      }
    },
    onInputKeyEnter() {
      if (!this.results) return;

      const result = this.results[this.activeResult];
      if (result) {
        location.go(result.url);
      }
    },
    onTouchStart() {
      this.$refs.input.blur();
    }
  },
  i18n: {
    messages: {
      en: {
        placeholder: "Search for titles and authors",
        noResults: "No results found. Please try a different search term.",
        by: "by",
        error: "Something went wrong."
      },
      de: {
        placeholder: "Suche nach Titel oder Autor",
        noResults: "Die Suche ergab keinen Treffer.",
        by: "von",
        error: "Etwas ist schief gelaufen."
      }
    }
  }
};
</script>

<style lang="scss">
.search {
  $root: &;
  $width-search: 33rem;
  $padding-header-top: 1.75rem;
  $padding-header-bottom: 1.75rem;

  &#{&} {

    @include media(max-width, m) {
      margin-left: $spacing-24;
    }

    @include media(max-width, xs) {
      margin-left: $spacing-16;
    }
  }

  &__header {
    display: flex;
    flex-grow: 1;
    align-items: center;

    @include media(max-width, m) {
      position: relative;
    }
  }

  &__trigger {
    margin-top: $spacing-4;

    cursor: pointer;

    &:hover {
      color: $blue;
    }
  }

  &__field {
    position: relative;

    display: flex;
    flex-grow: 1;
    align-items: center;
    margin-left: $spacing-8;
    padding: $spacing-8;

    &:before,
    &:after {
      content: "";

      position: absolute;
      right: 0;
      bottom: 0;
      left: 0;

      display: block;
      height: 2px;

      background-color: $light-grey;
    }

    &:after {
      display: none;

      background-color: $green;
    }

    @include media(min-width, m) {
      margin-left: $spacing-16;
    }

    @include media(min-width, l) {
      margin-left: $spacing-24;
    }
  }

  &__input {
    @include font-style(p, $color: $deep-black);

    flex-grow: 1;
  }

  &__close {
    display: block;
    width: $spacing-16;
    height: $spacing-16;
    margin-left: $spacing-8;

    color: $light-grey;

    cursor: pointer;

    &:hover {
      color: $blue;
    }
  }

  &__close-sprite {
    width: 100%;
  }

  &__menu {
    width: 100%;

    @include media(max-width, m) {
      position: fixed;
      top: 0;
      bottom: 0;
      left: 0;
      z-index: 2001; // More that smartbanner

      width: 100vw;
      height: auto;
      overflow: auto;
    }
  }

  &__menu-inner {
    width: 100%;
    padding-bottom: $spacing-8;

    background-color: $white;
    border-bottom-right-radius: $border-radius-4;
    border-bottom-left-radius: $border-radius-4;
  }

  &__no-results {
    @include font-style(p);

    padding: $spacing-16 $spacing-32;
  }

  &__error {
    @include font-style(error);

    padding: $spacing-16 $spacing-32;
  }

  &__result {

    /* stylelint-disable-next-line selector-nested-pattern */
    em {
      font-style: inherit;
    }

    &--active {
      background-color: $pale-mint-grey;
    }
  }

  &__link {
    display: block;

    #{$root}__result--active &,
    &:focus {
      background-color: $pale-mint-grey;
      outline: none;
    }

    @include media(max-width, m) {
      padding-right: map-get($container-paddings, s);
      padding-left: map-get($container-paddings, s);
    }

    @include media(max-width, s) {
      padding-right: map-get($container-paddings, xs);
      padding-left: map-get($container-paddings, xs);
    }

    @include media(min-width, m) {
      padding: 0 $spacing-24;
    }
  }

  &__link-inner {
    padding: $spacing-16;

    #{$root}__result:not(:last-child) & {
      border-bottom: $light-grey solid 1px;
    }
  }

  &__title {
    @include font-style(subheadline);

    display: inline;
    margin: 0 $spacing-8 0 0;
  }

  &__author {
    @include font-style(caption);
  }

  &--active {

    #{$root} {

      &__trigger {
        color: $blue;
      }
    }

    @include media(max-width, m) {
      position: fixed;
      right: 0;
      bottom: 0;
      left: 0;
      z-index: 1;

      display: block;

      background-color: $white;

      #{$root}__header {
        flex-grow: 0;
        box-sizing: content-box;
        height: 1rem;
        padding-top: $padding-header-top;
        padding-right: map-get($container-paddings, s);
        padding-bottom: $padding-header-bottom;
        padding-left: map-get($container-paddings, s);

        &:after {
          content: "";

          position: absolute;
          bottom: 0;
          left: 0;

          display: block;
          width: 100vw;
          height: 1px;

          background-color: $pale-mint-grey;
        }
      }

      &#{&} {
        margin: 0;
      }
    }

    @include media(max-width, s) {

      #{$root}__header {
        padding-right: map-get($container-paddings, xs);
        padding-left: map-get($container-paddings, xs);
      }
    }

    @include media(min-width, m) {
      flex-grow: 1;

      #{$root}__header {
        padding-right: $spacing-24;
      }
    }
  }

  &--loading {

    #{$root}__field:after {
      display: block;

      animation: 1s infinite alternate ease-in-out loading;
    }
  }

  @include media(min-width, m) {
    display: flex;
    align-items: center;
    max-width: $width-search;
  }

  @keyframes loading {

    0% {
      right: 90%;
      left: 0;
    }

    50% {
      right: 0;
      left: 0;
    }

    100% {
      right: 0;
      left: 90%;
    }
  }
}
</style>
